import api from './api';
import { ApiResponse, AttendanceStatus } from '../types';

// Types for My Attendance API responses
export interface MyAttendanceRecord {
  date: string;
  dayName: string;
  status: AttendanceStatus | 'LEAVE' | null;
  informationType?: string;
  remarks?: string;
  // New punch-based fields
  firstPunch?: string;
  lastPunch?: string;
  totalWorkMinutes?: number;
  lateByMinutes?: number;
  punchCount?: number;
  leaveType?: string;
  isOnLeave?: boolean;
}

export interface MyAttendanceSummary {
  totalDays: number;
  present: number;
  late: number;
  halfDay: number;
  absent: number;
  scheduledOff: number;
  leaves: number;
  attendancePercentage: number;
  totalWorkMinutes?: number;
  avgWorkMinutesPerDay?: number;
}

export interface WorkSettings {
  workStartTime: string;
  workEndTime: string;
  lateThresholdMinutes: number;
  halfDayThresholdMinutes: number;
}

export interface MyAttendanceData {
  year: number;
  month: number;
  monthName: string;
  records: MyAttendanceRecord[];
  summary: MyAttendanceSummary;
  workSettings?: WorkSettings;
}

export interface MonthlyTrend {
  month: number;
  monthName: string;
  year: number;
  present: number;
  late: number;
  halfDay: number;
  absent: number;
  leaves: number;
  totalDays: number;
  attendancePercentage: number;
  totalWorkMinutes?: number;
}

export interface BreakdownItem {
  name: string;
  value: number;
  color: string;
}

export interface MyAttendanceSummaryData {
  year: number;
  summary: MyAttendanceSummary;
  monthlyTrends: MonthlyTrend[];
  breakdown: BreakdownItem[];
}

class MyAttendanceService {
  private baseUrl = '/my-attendance';

  /**
   * Get current user's attendance for a specific month
   */
  async getMyAttendance(year: number, month: number): Promise<MyAttendanceData> {
    const response = await api.get<ApiResponse<MyAttendanceData>>(
      `${this.baseUrl}?year=${year}&month=${month}`
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message);
    }
    
    return response.data.data!;
  }

  /**
   * Get current user's attendance summary for the year (for charts)
   */
  async getMyAttendanceSummary(year: number): Promise<MyAttendanceSummaryData> {
    const response = await api.get<ApiResponse<MyAttendanceSummaryData>>(
      `${this.baseUrl}/summary?year=${year}`
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message);
    }
    
    return response.data.data!;
  }

  /**
   * Format minutes to hours:minutes string
   */
  static formatMinutesToDuration(minutes: number | undefined): string {
    if (minutes === undefined || minutes === null) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  /**
   * Format punch time for display (UTC to local)
   */
  static formatPunchTime(punchTime: string | undefined): string {
    if (!punchTime) return '-';
    const date = new Date(punchTime);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  }

  /**
   * Get status display name
   */
  static getStatusDisplayName(status: string | null): string {
    if (!status) return '-';
    switch (status) {
      case 'PRESENT': return 'Present';
      case 'LATE': return 'Late';
      case 'HALF_DAY': return 'Half Day';
      case 'ABSENT': return 'Absent';
      case 'SCHEDULED_OFF': return 'Day Off';
      default: return status;
    }
  }

  /**
   * Get status color for chips/badges
   */
  static getStatusColor(status: string | null): 'success' | 'warning' | 'info' | 'error' | 'default' {
    if (!status) return 'default';
    switch (status) {
      case 'PRESENT': return 'success';
      case 'LATE': return 'warning';
      case 'HALF_DAY': return 'info';
      case 'ABSENT': return 'error';
      case 'SCHEDULED_OFF': return 'default';
      default: return 'default';
    }
  }
}

export const myAttendanceService = new MyAttendanceService();
export default myAttendanceService;
